import { APIGet, APIPost, APIPut, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IOrder } from "redux/reducers/models/order";

interface IGetGiftCard {
  order_id?: number;
  code?: string;
  customer_id?: number;
  search?: string;
  filters?: Array<Record<string, any>>;
  view_permission?: boolean;
  gift_card_ids?: Array<number>;
  invoice_id?: number;
  last_reload_order_id?: number;
}

interface IPrintGiftCard {
  id: number;
  last4?: string;
}

interface IDisableGiftCard {
  id: number;
  expires_at?: string;
}

interface IPutGiftCard {
  code: string;
  note?: string;
  customer_id?: number;
  expires_at?: string;
  id?: number;
}

interface IPutGiftCardEmail {
  gift_card_id: number;
  customer_email: string;
}

export interface IGiftCard {
  admin_id: number;
  balance: number;
  client_id: number;
  code: string;
  currency: string;
  customer_id: number;
  disabled_at: string;
  expires_at: string;
  id: number;
  last4: string;
  line_item_id: number;
  note: string;
  order_id: number;
  pin: string;
  status: string;
  last_reload_order_id: number;
}

export interface ITransaction {
  id: number;
  order_id: number;
  order: IOrder;
  currency: string;
}

export interface IGiftCardLog {
  admin_user_id: number;
  change: number;
  /** Date string */
  created_at: string;
  /** Date string */
  deleted_at: string | null;
  gift_card_id: number;
  id: number;
  log_description: string;
  log_type: string;
  new_balance: number;
  previous_balance: number;
  source_order_transaction_id: number;
  transaction: ITransaction;
  updated_at: string;
  voided_at: string | null;
}

interface IDownloadGiftCard {
  id: number;
}


export async function GetGiftCardValidate(params: { code: string; reload: number }, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/validate"), params, useGlobalLoader);
}

export async function GetGiftCard(params: IGetGiftCard, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IGiftCard[]>(
    APIUrl(AdminType.FacilityAdmin, "/payment/gift-card"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PrintGiftCard(params: IPrintGiftCard, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/print"), params, useGlobalLoader);
}

export async function PutDisableGiftCard(params: IDisableGiftCard, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/disable"), params, useGlobalLoader);
}

export async function PutEnableGiftCard(params: IDisableGiftCard, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/enable"), params, useGlobalLoader);
}

export async function PutGiftCard(params: IPutGiftCard, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card"), params, useGlobalLoader);
}

export async function PutGiftCardEmail(params: IPutGiftCardEmail, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/email"), params, useGlobalLoader);
}

export async function GetGiftCardHistory(
  params: { gift_card_id: number },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<IGiftCardLog[]>(
    APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/log"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PrintGiftCardLog(params: IPrintGiftCard, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/statement"), params, useGlobalLoader);
}

/**
 * Including parent_gift_card_id in gift_card_ids array will produce a card with mismatched values.
 */
export async function MergeGiftCards(
  params: { parent_gift_card_id: number; gift_card_ids: number[] },
  useGlobalLoader: boolean,
) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/merge"), params, useGlobalLoader);
}

export async function ImportGiftCards(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/import"), params, useGlobalLoader);
}

export async function DownloadGiftCard(params: IDownloadGiftCard, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/payment/gift-card/download"), params, useGlobalLoader);
}
